<template>
    <div class="report-menu">
        <div
            class="menu-item"
            v-for="item in menuItems"
            :key="item.tab"
            @click="updateQueryParams('report', item.tab)"
            :class="{ active: currentTab === item.tab }"
        >
            <div class="menu-item-icon" :class="item.iconClass">
                <component v-if="item.component" :is="item.component" v-bind="item.componentProps" />
                <i v-else class="material-icons">{{ item.icon }}</i>
            </div>
            <span class="menu-item-label">{{ item.label }}</span>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import ReportTabs from "./ReportTabs";
import WhatsappBusinessIcon from "../../Pages/Chatbot/pages/integrations/svg/WhatsappBusinessIcon.vue";
import { mapState } from "vuex";

export default Vue.extend({
    name: "ReportMenu",
    computed: {
        ReportTabs() {
            return ReportTabs;
        },
        ...mapState({
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
        }),
        menuItems() {
            return [
                { tab: ReportTabs.OVERALL, icon: "trending_up", iconClass: "icon-overall", label: this.$t("report.overall") },
                { tab: ReportTabs.CHAT_DISTRIBUTIONS, icon: "donut_small", iconClass: "icon-chat-dist", label: this.$t("report.chatDistributions") },
                { tab: ReportTabs.CONTACT_DISTRIBUTIONS, icon: "contacts", iconClass: "icon-contact-dist", label: this.$t("report.contactDistributions") },
                { tab: ReportTabs.AGENT_PERFORMANCE, icon: "speed", iconClass: "icon-agent-perf", label: this.$t("report.agentPerformance") },
                { tab: ReportTabs.AGENT_ACTIVITIES, icon: "wifi", iconClass: "icon-agent-act", label: this.$t("report.dailyAgentActivity") },
                { tab: ReportTabs.AGENT_WORK_TIME, icon: "history_toggle_off", iconClass: "icon-work-time", label: this.$t("report.teamBreakAnalysis") },
                { tab: ReportTabs.CUSTOMER_SATISFACTION, icon: "sentiment_very_satisfied", iconClass: "icon-satisfaction", label: this.$t("report.customerSatisfaction") },
                { tab: ReportTabs.WEBSITE_ANALYSIS, icon: "query_stats", iconClass: "icon-website", label: this.$t("report.websiteAnalysis") },
                { tab: ReportTabs.CHANNEL_BREAKDOWN, icon: "hub", iconClass: "icon-channel", label: this.$t("report.channelBreakdown") },
                { tab: ReportTabs.WHATSAPP_BUSINESS_API, icon: null, iconClass: "icon-whatsapp", label: "WhatsApp Business API", component: "WhatsappBusinessIcon", componentProps: { style: "width: 18px; height: 18px", color: "#fff" } },
                { tab: ReportTabs.Calendar, icon: "calendar_month", iconClass: "icon-calendar", label: this.$t("message.calendar") },
                { tab: ReportTabs.Mail, icon: "mail_outline", iconClass: "icon-mail", label: this.$t("message.mails") },
                { tab: ReportTabs.Ticket, icon: "confirmation_number", iconClass: "icon-ticket", label: this.$t("message.ticket") },
                { tab: ReportTabs.TASK, icon: "task_alt", iconClass: "icon-task", label: this.$t("task.workflow") },
            ];
        },
    },
    components: { WhatsappBusinessIcon },
    props: {
        stats: {
            type: Object,
            default: () => ({}),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        updateQueryParams: {
            type: Function,
            default: () => {},
        },
        currentTab: {
            type: String,
            default: "",
        },
    },
});
</script>

<style scoped lang="scss">
.report-menu {
    position: sticky;
    top: 0;
    overflow-y: auto;
    height: 100%;
    min-width: 220px;
    max-width: 220px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
    padding: 6px 6px 10px;
    display: flex;
    flex-direction: column;
    gap: 1px;

    // Subtle scrollbar for small screens
    &::-webkit-scrollbar {
        width: 3px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.12);
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.2);
    }
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.12) transparent;

    .menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 8px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover {
            background: #f0f4ff;
        }

        &.active {
            background: #eff6ff;

            .menu-item-icon {
                transform: scale(1.05);
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
            }

            .menu-item-label {
                color: #1e40af;
                font-weight: 600;
            }
        }

        .menu-item-icon {
            width: 28px;
            height: 28px;
            min-width: 28px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.15s ease;

            i {
                font-size: 16px !important;
                color: #fff;
            }

            svg {
                width: 16px;
                height: 16px;
            }

            &.icon-overall { background: linear-gradient(135deg, #3b82f6, #2563eb); }
            &.icon-chat-dist { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
            &.icon-contact-dist { background: linear-gradient(135deg, #06b6d4, #0891b2); }
            &.icon-agent-perf { background: linear-gradient(135deg, #f59e0b, #d97706); }
            &.icon-agent-act { background: linear-gradient(135deg, #10b981, #059669); }
            &.icon-work-time { background: linear-gradient(135deg, #6366f1, #4f46e5); }
            &.icon-satisfaction { background: linear-gradient(135deg, #ec4899, #db2777); }
            &.icon-website { background: linear-gradient(135deg, #14b8a6, #0d9488); }
            &.icon-channel { background: linear-gradient(135deg, #f97316, #ea580c); }
            &.icon-whatsapp { background: linear-gradient(135deg, #25d366, #128c7e); }
            &.icon-calendar { background: linear-gradient(135deg, #3b82f6, #1d4ed8); }
            &.icon-mail { background: linear-gradient(135deg, #ef4444, #dc2626); }
            &.icon-ticket { background: linear-gradient(135deg, #a855f7, #9333ea); }
            &.icon-task { background: linear-gradient(135deg, #64748b, #475569); }
        }

        .menu-item-label {
            font-size: 12.5px;
            font-weight: 500;
            color: #374151;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.2;
        }
    }
}

.dark-mode {
    .report-menu {
        background: #1e1e2d;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.12);
        }
        &::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.2);
        }
        scrollbar-color: rgba(255, 255, 255, 0.12) transparent;

        .menu-item {
            &:hover {
                background: rgba(255, 255, 255, 0.06);
            }

            &.active {
                background: rgba(59, 130, 246, 0.12);

                .menu-item-label {
                    color: #93bbfc;
                }
            }

            .menu-item-label {
                color: #9ca3af;
            }
        }
    }
}
</style>